import { Component, Inject, OnInit, Input, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Gift } from "@interfaces/dws/gift";
import { TranslateDomainPipe } from "@web/app/pipes/translate/translate-domain.pipe";
import { Reservation } from "@interfaces/reservation";
import * as moment from 'moment';
import { tr } from "@util/tr";
import { locale } from "@util/locale";
import { capitalize, set } from "lodash";
import { environment } from '@env/environment';
import { ResendGiftEmail } from '@web/app/components/dialogs/resend-gift-email/resend-gift-email.component';
import { ga } from "@util/ga";
import { ExperiencePriceLabel } from "@interfaces/experience-price-label";
import { NoticeComponent } from '../notice/notice.component';
import { TranslateService } from '@ngx-translate/core';
import { SendGiftEmail } from '../send-gift-email/send-gift-email.component';
import { observe } from '@web/util/loading/loading';
import { GiftsService } from "@services/dws/gifts.service";
import { GiftLanguageChooserComponent } from '../gift-language-chooser/gift-language-chooser.component';
import { MatDatepicker } from '@angular/material/datepicker';
import { toast } from '@web/util/toast';
import { CashRegisterService, SyncHelperService } from '@services/dws';
import { IntegrationEntityTypes } from '@interfaces/integration-sync-metadata';
import { DomSanitizer } from '@angular/platform-browser';
import { GiftRedeemComponent } from '../gift-redeem/gift-redeem.component';

@Component({
  selector: 'app-gift-detail',
  templateUrl: './gift-detail.component.html',
  styleUrls: ['./gift-detail.component.scss']
})
export class GiftDetailComponent implements OnInit {
  gift!: Gift;
  reservation?: Reservation;
  form: FormGroup;
  isRedeemed: boolean = false;
  priceLabelsFromExperience!: ExperiencePriceLabel[];
  language: string = "";
  dateInfoVisibility: boolean = false;
  @ViewChild('sendingDatePicker') sendingDatePicker!: MatDatepicker<Date>;
  @ViewChild('expirationDatePicker') expirationDatePicker!: MatDatepicker<Date>;
  regCashIntegrated: boolean = false;
  isLoadingRedeem: boolean = false;
  isSaving: boolean = false;
  staffNoteDraft = '';
  experiencePriceLabels: FormArray;
  syncButtons: any[] = [];

  get cashRegisterSync$() {
    return this.cashRegisterService
      .syncGift(this.gift.wineryId!, this.gift!.giftId!.toString());
  }

  get cashRegisterStatus$() {
    return this.cashRegisterService
    .getMetadata(IntegrationEntityTypes.GIFT, this.gift!.id!.toString());
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dlg: MatDialogRef<GiftDetailComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    public translate: TranslateService,
    private giftsService: GiftsService,
    private cashRegisterService: CashRegisterService,
    private sanitizer: DomSanitizer,
    private syncHelper: SyncHelperService,
  ) {
    this.language = translate.getDefaultLang();
    this.gift = data.gift;
    this.returnToReservation = data.returnToReservation;
    const title = this.gift?.extraData?.experience?.title;
    const experienceTitle = (title && title[this.language as keyof typeof title]) ||
                           (title && title['en']) ||
                           '';    const buyerName = `${this.gift?.firstname ?? ''} ${this.gift?.lastname ?? ''}`.trim();
    const buyerEmail = this.gift?.email || '';
    const beneficiaryName = `${this.gift?.beneficiaryFirstname ?? ''} ${this.gift?.beneficiaryLastname ?? ''}`.trim();
    const beneficiaryEmail = this.gift?.beneficiaryEmail;
    const purchasedDate = this.gift?.createdAt ? moment(this.gift?.createdAt).format('DD/MM/YYYY') : "";
    const origin = this.gift?.extraData.origin;
    const sendingDate = this.gift?.beneficiaryNotifyAt ? new Date(this.gift?.beneficiaryNotifyAt) : null;
    const price01 = this.gift?.adults;
    const price02 = this.gift?.children;
    const expirationDate = this.gift?.expiresAt ? new Date(this.gift?.expiresAt) : null;
    const redemptionDate = this.gift?.redemptionDate ? moment(this.gift?.redemptionDate).format('DD/MM/YYYY') : "";
    const reservationId = this.gift?.reservationId;
    const status = tr(capitalize(this.gift?.aasmState));
    const discountCode = this.gift?.extraData?.coupon?.code;
    const discount = (this.gift?.extraData?.coupon)!.amountCents / 100;
    const method = this.gift?.extraData?.order?.paymentMethod;
    const total = this.gift?.amountCents / 100;



    this.form = this.fb.group({
      experienceTitle: [experienceTitle],
      buyerName: [buyerName],
      buyerEmail: [buyerEmail],
      beneficiaryName: [beneficiaryName],
      beneficiaryEmail: [beneficiaryEmail],
      purchasedDate: [purchasedDate],
      origin: [origin],
      sendingDate: [sendingDate],
      price01: [price01],
      price02: [price02],
      expirationDate: [expirationDate],
      redemptionDate: [redemptionDate],
      reservationId: [reservationId],
      status: [status],
      discountCode: [discountCode],
      discount: [discount],
      method: [method],
      total: [total],
    });

    this.experiencePriceLabels = this.fb.array([]);

    const control = this.formPriceLabelsArray;
    const priceLabels = this.gift?.experiencePriceLabels;
    priceLabels.forEach(pl => {
      control.push(
        this.createFormGroup(this.fb, pl)
      )
    });

    this.validateGiftSendingDate = this.validateGiftSendingDate.bind(this);
    this.validateGiftExpirationDate = this.validateGiftExpirationDate.bind(this);
    this.giftsService = giftsService;

    this.cashRegisterService
      .isIntegrated()
      .subscribe((x) => {
        this.regCashIntegrated = x;
      });

    // Load sync buttons for this gift
    if (this.gift?.giftId && this.gift?.wineryId) {
      this.syncHelper.getAvailableSyncButtons$(
        this.gift.wineryId,
        IntegrationEntityTypes.GIFT,
        this.gift.giftId.toString()
      ).subscribe(buttons => {
        this.syncButtons = buttons;
      });
    }
  }

  @Input() returnToReservation?: boolean;

  ngOnInit() {
    ga.track(`Gift ${this.gift?.giftId}`);
    if (this.gift?.aasmState === 'redeemed') {
      this.isRedeemed = true;
    }
    const sn = this.gift?.extraData && (this.gift.extraData as any).staffNote;
    this.staffNoteDraft = typeof sn === 'string' ? sn : '';
  }

  private currentStaffNote(): string {
    const sn = this.gift?.extraData && (this.gift.extraData as any).staffNote;
    return typeof sn === 'string' ? sn : '';
  }

  isStaffNoteEdited(): boolean {
    return (this.staffNoteDraft || '') !== this.currentStaffNote();
  }

  private isGiftFieldsEdited(): boolean {
    return this.isBeneficiaryNameEdited() || this.isBeneficiaryEmailEdited() || this.isSendingDateEdited() || this.isExpirationDateEdited();
  }

  private applyStaffNoteLocally(updated?: Gift): void {
    if (updated?.extraData) {
      this.gift.extraData = updated.extraData;
    } else if (this.gift.extraData) {
      if (this.staffNoteDraft) {
        (this.gift.extraData as any).staffNote = this.staffNoteDraft;
      } else {
        delete (this.gift.extraData as any).staffNote;
      }
    }
    this.staffNoteDraft = this.currentStaffNote();
  }

  private saveStaffNote$() {
    return this.giftsService.putStaffNote(this.gift.giftId, this.staffNoteDraft || '');
  }

  isExpired() {
    return new Date(this.gift.expiresAt) < new Date() && this.gift.aasmState !== 'redeemed';
  }

  get beneficiaryAlreadyNotified(): boolean {
    return moment(this.gift?.beneficiaryNotifyAt) <= moment() ? true : false;
  }

  redeemGift() {
    this.isLoadingRedeem = true;
    if (this.isExpired()) {
      this.isLoadingRedeem = false;
      this.translate
        .get('The gift expired on {expirationDate}. It cannot be redeemed.', {
          expirationDate: moment(this.gift?.expiresAt).format('DD/MM/YYYY'),
        })
        .subscribe(async (res: string) => {
          toast(tr(res));
        });
    } else {
      this.isLoadingRedeem = false;
      this.openGiftRedeemDialog(this.gift);
    }
  }

  closeClicked() {
    this.dlg.close({ loadReservation: false });
  }

  openReservation() {
    this.dlg.close({ loadReservation: true })
  }

  validateGiftSendingDate(d: Date): boolean {
    if (d == null) return false;
    let date = moment(d);
    return date.isSameOrAfter(moment(), 'day') && date.isSameOrBefore(moment(this.gift.expiresAt), 'day');
  }

  validateGiftExpirationDate(d: Date): boolean {
    if (d == null) return false;
    let date = moment(d);
    let purchaseDate = moment(this.gift.createdAt);
    return date.isSameOrAfter(purchaseDate, 'day');
  }

  openGiftDetails(gift: Gift) {
    const dialog = this.dialog.open(GiftDetailComponent, {
      width: '50%',
      minWidth: 1000,
      maxWidth: 1000,
      data: {
        gift
      }
    });

    dialog.afterClosed().subscribe(res => {
    });
  }

  openGiftRedeemDialog(gift: Gift) {
    const dialog = this.dialog.open(GiftRedeemComponent, {
      width: "50%",
      minWidth: 720,
      maxWidth: 720,
      data: {
        gift,
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (!res) {
        this.isLoadingRedeem = false;
      } else if (res.action === 'link') {
        this.isLoadingRedeem = false;
        const site = environment.values.divinea;
        const experienceId = gift.extraData.experience.id;
        const giftId = gift.giftId;
        const link = `https://${site}/esperienza/${experienceId}?redeem_code=${giftId}`;
        window.open(link);
      } else if (res.action === 'redeemed') {
        setTimeout(() => window.location.reload(), 4000);
      }
    });
  }

  price01label() {
    var priceLabel = this.gift.extraData.experience.priceLabel01
    if (priceLabel == null) return tr('Price 1')
    return locale().locale == 'it' ? priceLabel.it : priceLabel.en
  }

  price02label() {
    var priceLabel = this.gift.extraData.experience.priceLabel02
    if (priceLabel == null) return tr('Price 2')
    return locale().locale == 'it' ? priceLabel.it : priceLabel.en
  }

  get formPriceLabelsArray() {
    return this.experiencePriceLabels.controls as FormGroup[];
  }

  createFormGroup(fb: FormBuilder, ex?: Partial<ExperiencePriceLabel>) {
    const f = fb.group({
      id: [''],
      quantity: [''],
      title_translations: [''],
      experience_id: ['']
    })
    if (ex) {
      f.patchValue({
        ...ex,
        'quantity': ex.quantity,
        'title_en': ex.title_translations?.en,
        'title_it': ex.title_translations?.it,
        'title_de': ex.title_translations?.de,
        experience_id: ex.experience_id
      });
    }
    return f;
  }

  async resendEmail() {
    const dlg = this.dialog.open(ResendGiftEmail, {
      width: '50%',
      minWidth: '45%',
      maxWidth: '45%',
      data: {
        giftId: this.gift.giftId,
        giftEmail: this.gift.beneficiaryEmail
      }
    })

    await dlg.afterClosed().toPromise();

  }

  async sendGiftEmail() {
    let name = this.form.value.beneficiaryName.split(' ');
    let firstname: string = name.shift();
    let lastname: string = name.shift();
    name.forEach((n: string) => lastname = lastname + ' ' + n);

    const dlg = this.dialog.open(SendGiftEmail, {
      width: '50%',
      minWidth: '45%',
      maxWidth: '45%',
      data: {
        giftId: this.gift.giftId,
        giftEmail: this.form.value.beneficiaryEmail,
      }
    })
  }

  updateGift() {
    if (this.staffNoteDraft.length > 500) {
      toast(tr('Staff notes are limited to 500 characters'));
      return;
    }

    const noteEdited = this.isStaffNoteEdited();
    const giftFieldsEdited = this.isGiftFieldsEdited();
    if (!noteEdited && !giftFieldsEdited) {
      return;
    }

    this.isSaving = true;

    if (!giftFieldsEdited) {
      this.saveStaffNote$().subscribe({
        next: (updated: Gift) => {
          this.isSaving = false;
          this.applyStaffNoteLocally(updated);
          toast(tr('Staff note saved.'));
        },
        error: () => {
          this.isSaving = false;
          toast(tr('Could not save staff note. Please try again.'));
        }
      });
      return;
    }

    const name = this.form.value.beneficiaryName.split(' ');
    const firstname: string = name.shift();
    let lastname: string = name.shift();
    name.forEach((n: string) => lastname = lastname + ' ' + n);
    const sendingDay = moment(this.form.value.sendingDate).format('YYYY-MM-DD');
    const oldSendingDay = moment(this.gift.beneficiaryNotifyAt).format('YYYY-MM-DD');
    const expirationDay = moment(this.form.value.expirationDate).format('YYYY-MM-DD');

    if (moment(this.form.value.expirationDate).isBefore(moment(this.form.value.sendingDate), 'day')) {
      this.isSaving = false;
      toast(tr("The expiration date cannot be before the sending date."));
      return;
    }

    this.giftsService.updateCrm(
      this.gift.giftId,
      this.form.value.beneficiaryEmail,
      sendingDay,
      firstname,
      lastname,
      expirationDay
    ).subscribe({
      next: () => {
        const finish = () => {
          toast(
            sendingDay == moment().format('DD/MM/YYYY') && oldSendingDay != moment().format('DD/MM/YYYY')
              ? tr("The gift has been updated and the email has been sent successfully")
              : tr("Gift updated successfully")
          );
          setTimeout(() => window.location.reload(), 4000);
        };

        if (!noteEdited) {
          finish();
          return;
        }

        this.saveStaffNote$().subscribe({
          next: (updated: Gift) => {
            this.applyStaffNoteLocally(updated);
            finish();
          },
          error: () => {
            this.isSaving = false;
            toast(tr('Could not save staff note. Please try again.'));
          }
        });
      },
      error: err => {
        this.isSaving = false
        console.error("Error trying to update gift", err);
        toast(tr("An error occurred while trying to update the gift. Please try again later."));
      }
    });
  }

  isBeneficiaryNameEdited(): boolean {
    return this.form.value.beneficiaryName !== this.gift?.beneficiaryFirstname + ' ' + this.gift?.beneficiaryLastname;
  }

  isBeneficiaryEmailEdited(): boolean {
    return this.form.value.beneficiaryEmail !== this.gift?.beneficiaryEmail;
  }

  isSendingDateEdited(): boolean {
    return !moment(this.form.value.sendingDate).isSame(moment(this.gift?.beneficiaryNotifyAt), 'day');
  }

  isExpirationDateEdited(): boolean {
    return !moment(this.form.value.expirationDate).isSame(moment(this.gift?.expiresAt), 'day');
  }

  isEdited(): boolean {
    return this.isGiftFieldsEdited() || this.isStaffNoteEdited();
  }

  cancelEditing() {
    this.form.patchValue({
      beneficiaryName: this.gift.beneficiaryFirstname + ' ' + this.gift.beneficiaryLastname,
      beneficiaryEmail: this.gift.beneficiaryEmail,
      sendingDate: new Date(this.gift.beneficiaryNotifyAt),
      expirationDate: new Date(this.gift.expiresAt)
    });
    this.staffNoteDraft = this.currentStaffNote();
  }

  getGiftVoucher() {
    this.openLanguageDialog();
  }

  openLanguageDialog(): void {
    const dialogRef = this.dialog.open(GiftLanguageChooserComponent, {
      width: '450px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // User selected a language, use it in your code
        var lang = result;
        if (!!!!lang) {
          var giftId = this.gift.giftId;
          var wineryId = this.gift.wineryId;

          observe(this.giftsService.getPDF(wineryId!, giftId, lang))
            .subscribe((data: Blob) => {
              const url = URL.createObjectURL(data);

              // Create a link and trigger the download
              const link = document.createElement('a');
              link.href = url;
              link.download = 'gift_voucher_' + giftId + '.pdf';
              document.body.appendChild(link);
              link.click();

              // Cleanup: Remove the link and revoke the URL
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            });
        }
      }
    });
  }

  get invoiceKeys() {
    let k = Object.keys(this.gift.extraData?.invoiceData || {});
    // impose manual order (shared with reservation billing block)
    return [
      "firstName",
      "lastName",
      "companyName",
      "vat",
      "fiscalCode",
      "sdi",
      "billingCountry",
      "billingAddress",
      "billingCity",
      "billingProvince",
      "billingZipCode",
    ].filter((x) => k.includes(x));
  }

  // Getter pubblici per i valori readonly degli input nel template
  get experienceTitleValue(): string {
    return this.form.get('experienceTitle')?.value || '';
  }

  get buyerNameValue(): string {
    return this.form.get('buyerName')?.value || '';
  }

  get buyerEmailValue(): string {
    return this.form.get('buyerEmail')?.value || '';
  }

  get purchasedDateValue(): string {
    return this.form.get('purchasedDate')?.value || '';
  }

  get originValue(): string {
    return this.form.get('origin')?.value || '';
  }

  get price01Value(): number {
    return this.form.get('price01')?.value || 0;
  }

  get price02Value(): number {
    return this.form.get('price02')?.value || 0;
  }

  get redemptionDateValue(): string {
    return this.form.get('redemptionDate')?.value || '';
  }

  get reservationIdValue(): string {
    return this.form.get('reservationId')?.value || '';
  }

  get discountCodeValue(): string {
    return this.form.get('discountCode')?.value || '';
  }

  get discountValue(): number {
    return this.form.get('discount')?.value || 0;
  }

  get methodValue(): string {
    return this.form.get('method')?.value || '';
  }

  get statusValue(): string {
    return this.form.get('status')?.value || '';
  }

  get totalValue(): number {
    return this.form.get('total')?.value || 0;
  }

  get expirationDateValue(): any {
    return this.form.get('expirationDate')?.value;
  }

  get expirationDateFormattedValue(): string {
    const date = this.form.get('expirationDate')?.value;
    if (!date) return '';
    return moment(date).format('DD/MM/YYYY');
  }

  // Funzioni per gestire lo stato con ws-chip (stessa logica di gifts.component)
  getLabelForStatus(): string {
    if (this.isExpired()) {
      return 'Expired';
    } else {
      if (this.gift.aasmState === 'paid') {
        return 'To Be Redeemed';
      } else {
        return this.gift.aasmState;
      }
    }
  }

  statusBgColorFn(): string {
    const status = this.gift.aasmState;
    switch (status) {
      case "draft":
        return "draft";
      case "expired":
        return "danger";
      case "paid":
        return "success";
      case "redeemed":
        return "success_dark";
      default:
        return "secondary";
    }
  }

  openBillingData() {
    if (!this.invoiceKeys) return;

    let text = tr("Billing Data") + ":";
    let items = this.invoiceKeys.map((x) => this.sanitizer.bypassSecurityTrustHtml(
      `<div style="display: inline-flex; justify-content: space-between; width: 100%;"><div>${
        tr('invoiceFields.' + x)
      }:</div><div>${
        this.gift.extraData.invoiceData[x]?.nameIt
          ? TranslateDomainPipe.transform( this.gift.extraData.invoiceData[x] )
          : this.gift.extraData.invoiceData[x]
      }</div></div>`
    ));

    this.dialog.open(NoticeComponent, {
      minWidth: 550,
      maxWidth: 550,
      panelClass: "no-overflow",
      data: {
        text,
        items,
      },
    });
  }
}